/**
 * ELF-CIRCLE — Shop.
 *
 * The overview asks for a real storefront rather than a bolt-on shop section,
 * with merch drops timed to the two owned properties. The homepage band sells
 * the current drop; this page is the storefront behind it.
 *
 * Signature mechanism: the drop rail. Products are organised by drop rather
 * than by garment type — selecting a drop filters the grid *and* surfaces the
 * event it was cut for, which is the actual merchandising logic (a drop exists
 * because a room exists). The homepage band filters by category; this does not
 * repeat that.
 *
 * The catalogue below is the Shopify seam. Product data, inventory and pricing
 * are replaced wholesale by the Storefront API, and checkout deliberately never
 * completes in this page — payment stays inside Shopify/Stripe's own secure
 * flow. Until that integration lands, `STOREFRONT_LIVE` keeps the bag honest:
 * it captures interest instead of pretending to take money.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import hoodieBlack from '../../../../../assets/elf/merch-hoodie-black.jpg';
import hoodieBlue from '../../../../../assets/elf/merch-hoodie-blue.jpg';
import hoodieGrey from '../../../../../assets/elf/merch-hoodie-grey.jpg';
import teeBlack from '../../../../../assets/elf/merch-tee-black.jpg';
import circuitArt from '../../../../../assets/elf/shop-circuit.jpg';
import coreArt from '../../../../../assets/elf/shop-core.jpg';
import statewideArt from '../../../../../assets/elf/shop-statewide.jpg';
import hoodieStatewide from '../../../../../assets/elf/merch-hoodie-statewide.jpg';
import teeCounty from '../../../../../assets/elf/merch-tee-county.jpg';
import teeMarkNatural from '../../../../../assets/elf/merch-tee-mark-natural.jpg';
import teeRoom from '../../../../../assets/elf/merch-tee-room.jpg';

/**
 * Flips to `true` when the Shopify Storefront API is wired. Until then the bag
 * captures interest rather than routing to a checkout that does not exist —
 * a dead "Checkout" button is worse than an honest one.
 */
const STOREFRONT_LIVE = false;

interface Drop {
  id: string;
  name: string;
  /** The room the drop was cut for. */
  tied: string;
  /** Where that room lives on the site. */
  to: string;
  when: string;
  note: string;
}

interface Product {
  /** Stable identity — becomes the Shopify product handle. */
  handle: string;
  name: string;
  drop: string;
  image: string;
  price: number;
  desc: string;
  tag?: string;
  sizes: { label: string; inStock: boolean }[];
}

const DROPS: Drop[] = [
  {
    id: 'circuit',
    name: 'Circuit',
    tied: 'Monthly Networking Series',
    to: '/networking-events',
    when: 'Rolling — every market',
    note: 'Cut for the room that repeats. Heavyweight, unbranded enough to wear outside the event.',
  },
  {
    id: 'statewide',
    name: 'Statewide',
    tied: 'Made In New Jersey',
    to: '/made-in-new-jersey',
    when: 'Annual — festival edition',
    note: 'The festival edition. Produced against the 21-county programme and retired when the season closes.',
  },
  {
    id: 'core',
    name: 'Core',
    tied: 'Always on',
    to: '/who-we-are',
    when: 'Permanent',
    note: 'The pieces that are not tied to a date — the mark, plain, in the palette it was drawn in.',
  },
];

/** The picture behind the page's opening screen for each drop, keyed by drop id. */
const DROP_ART: Record<string, string> = {
  circuit: circuitArt,
  statewide: statewideArt,
  core: coreArt,
};

const ALL_SIZES = ['S', 'M', 'L', 'XL'];
const sizes = (...inStock: string[]) =>
  ALL_SIZES.map((label) => ({ label, inStock: inStock.includes(label) }));

/** Placeholder catalogue — replaced wholesale by the Shopify integration. */
const PRODUCTS: Product[] = [
  { handle: 'circuit-hoodie', name: 'Circuit Hoodie', drop: 'circuit', image: hoodieBlack, price: 98, desc: 'Black · heavyweight fleece', tag: 'New drop', sizes: sizes('S', 'M', 'L') },
  { handle: 'signal-hoodie', name: 'Signal Hoodie', drop: 'circuit', image: hoodieBlue, price: 98, desc: 'Cobalt · heavyweight fleece', sizes: sizes('M', 'L', 'XL') },
  { handle: 'room-tee', name: 'The Room Tee', drop: 'circuit', image: teeRoom, price: 44, desc: 'Black · boxy fit', sizes: sizes('S', 'M', 'L', 'XL') },
  { handle: 'statewide-hoodie', name: 'Statewide Hoodie', drop: 'statewide', image: hoodieStatewide, price: 104, desc: 'Heather · festival edition', tag: '21 counties', sizes: sizes('M', 'L') },
  { handle: 'county-tee', name: 'County Tee', drop: 'statewide', image: teeCounty, price: 48, desc: 'Natural · county list on reverse', sizes: sizes('S', 'M', 'L') },
  { handle: 'mark-tee', name: 'Mark Tee', drop: 'core', image: teeBlack, price: 40, desc: 'Black · the crest, small', sizes: sizes('S', 'M', 'L', 'XL') },
  { handle: 'mark-tee-white', name: 'Mark Tee — Natural', drop: 'core', image: teeMarkNatural, price: 40, desc: 'Natural · the crest, small', sizes: sizes('M', 'L', 'XL') },
  { handle: 'crest-hoodie', name: 'Crest Hoodie', drop: 'core', image: hoodieGrey, price: 92, desc: 'Heather · embroidered crest', sizes: sizes('S', 'L', 'XL') },
];

interface BagLine {
  handle: string;
  name: string;
  size: string;
  price: number;
}

export const Shop: React.FC = () => {
  const consult = useConsult();
  const [dropId, setDropId] = useState<string>('circuit');
  const [bag, setBag] = useState<BagLine[]>([]);

  const drop = DROPS.find((d) => d.id === dropId) ?? DROPS[0];
  const shown = useMemo(() => PRODUCTS.filter((p) => p.drop === dropId), [dropId]);
  const subtotal = bag.reduce((sum, line) => sum + line.price, 0);

  const add = (p: Product, size: string) =>
    setBag((b) => [...b, { handle: p.handle, name: p.name, size, price: p.price }]);
  const drop1 = (i: number) => setBag((b) => b.filter((_, n) => n !== i));

  const checkout = () =>
    consult?.open({
      source: 'shop.checkout',
      interest: 'Merch & Commerce',
      title: STOREFRONT_LIVE ? 'Checkout' : 'Tell me when the store opens',
      });

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/shop" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Shop']} />
          </div>

          {/* The opening screen — heading, drop rail and its note — sits on the
              selected drop's picture. The products below are Shopify's to
              supply; this imagery is the page's own. */}
          <div className="shop-stage">
            <span className="shop-stage__art" aria-hidden="true">
              {DROPS.map((d) => (
                <img key={d.id} src={DROP_ART[d.id]} alt="" data-on={d.id === dropId ? 'true' : undefined} />
              ))}
              <span />
            </span>
            <div className="shop-head">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                  <i />
                  Merchandising
                </span>
                <h1 className="elf-page__h1">Shop the drop.</h1>
                <p className="wbody" style={{ maxWidth: '56ch' }}>
                  Every drop is cut for a room — a market on the monthly rotation, or a season of the
                  statewide festival. Pick the drop and the shelf changes with it.
                </p>
              </div>
            </div>

            {/* --- signature: the drop rail --- */}
            <div className="shop-rail" role="tablist" aria-label="Drops">
              {DROPS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="tab"
                  aria-selected={d.id === dropId}
                  className="shop-drop"
                  onClick={() => setDropId(d.id)}
                >
                  <span className="statl">{d.when}</span>
                  <span className="pillarname">{d.name}</span>
                  <span className="wtag" style={{ fontSize: 12.5 }}>
                    {d.tied}
                  </span>
                  <span className="wbar" />
                </button>
              ))}
            </div>

            <div className="shop-tied">
              <p className="wbody" style={{ fontSize: 15, margin: 0 }}>
                {drop.note}
              </p>
              <Link className="pillghost" to={route(drop.to)}>
                {drop.tied} →
              </Link>
            </div>
          </div>

          {/* --- catalogue + bag --- */}
          <div className="shop-body">
            <div className="shop-grid">
              {shown.map((p) => (
                <article className="shop-card" key={p.handle}>
                  <div className="shop-card__art">
                    <img src={p.image} alt={p.name} />
                    {p.tag && <span className="shop-card__tag">{p.tag}</span>}
                  </div>
                  <div className="shop-card__meta">
                    <span className="whead" style={{ fontSize: 17 }}>
                      {p.name}
                    </span>
                    <span className="shop-card__price">${p.price}</span>
                  </div>
                  <span className="wtag" style={{ fontSize: 12.5 }}>
                    {p.desc}
                  </span>
                  <div className="shop-card__sizes">
                    {p.sizes.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        className="shop-size"
                        disabled={!s.inStock}
                        aria-label={
                          s.inStock ? `Add ${p.name}, size ${s.label}` : `${p.name}, size ${s.label} — sold out`
                        }
                        onClick={() => add(p, s.label)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </article>
              ))}
            </div>

            <aside className="shop-bag">
              <span className="colhead">The bag</span>
              {bag.length === 0 ? (
                <p className="wtag" style={{ fontSize: 13.5 }}>
                  Empty. Pick a size on any card and it lands here.
                </p>
              ) : (
                <ul className="shop-bag__lines">
                  {bag.map((line, i) => (
                    <li key={`${line.handle}-${line.size}-${i}`}>
                      <span>
                        {line.name} <i>· {line.size}</i>
                      </span>
                      <b>${line.price}</b>
                      <button type="button" aria-label={`Remove ${line.name}`} onClick={() => drop1(i)}>
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="shop-bag__sub">
                <span>Subtotal</span>
                <span className="shop-bag__total">${subtotal}</span>
              </div>
              <button type="button" className="pillcta mag shop-bag__go" onClick={checkout}>
                {STOREFRONT_LIVE ? 'Continue to secure checkout' : 'Tell me when the store opens'}{' '}
                <span>→</span>
              </button>
              <p className="shop-bag__note">
                {STOREFRONT_LIVE
                  ? 'Payment completes on Shopify’s secure checkout — card details are never entered on this site.'
                  : 'The storefront is not open yet. Leave a note and you will hear first when the drop goes live.'}
              </p>
            </aside>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/networking-events')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Where the drops land</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  The monthly rooms
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  National, market by market.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <Link className="nextlink" to={route('/made-in-new-jersey')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">The festival edition</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Made In New Jersey
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  21 counties, annually.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default Shop;
